import { useState, useMemo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ArrowLeft, Trophy, ArrowUp, ArrowDown, Clock, Gift } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import {
  useLeaderboard,
  useLeaderboardGames,
  useTimeGames,
  useTimePlayedLeaderboard,
  useRewardTypes,
  useRewardLeaderboard,
  useLeaderboardAnonymity,
} from '../hooks';

const backToDashboardLink = (
  <Link
    to="/a/arche_game_rewards"
    className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
  >
    <ArrowLeft className="h-4 w-4 mr-1" />
    Back to Dashboard
  </Link>
);

/** Turn a raw `game_event` key (e.g. `win_easy`) into a readable label. */
function formatEventLabel(event: string): string {
  return event.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Human-readable duration (e.g. `3h 12m`, `12m`). */
function formatTime(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** Rank cell: medals for the top 3, `#n` otherwise. */
function rankCell(rank: number): ReactNode {
  if (rank === 1) return <span className="text-lg">{'🥇'}</span>;
  if (rank === 2) return <span className="text-lg">{'🥈'}</span>;
  if (rank === 3) return <span className="text-lg">{'🥉'}</span>;
  return `#${rank}`;
}

/** Display name for a leaderboard row — "(hidden)" when the user opted out. */
function playerName(entry: { username: string | null; anonymous: boolean }): string {
  if (entry.anonymous) return '(hidden)';
  return entry.username ?? 'Unknown';
}

function PageShell({ children }: { children: ReactNode }) {
  return <div className="max-w-4xl mx-auto p-6 space-y-6">{children}</div>;
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
      <AlertCircle className="h-4 w-4 text-destructive" />
      <p className="text-sm text-destructive">{message}</p>
    </div>
  );
}

const selectClass = 'rounded-md border bg-background px-3 py-2 text-sm';

// ============================================================================
// Score leaderboard (existing behaviour, extracted)
// ============================================================================

function ScoreLeaderboard() {
  const { user } = useAuth();
  const { games, loading: gamesLoading, error: gamesError } = useLeaderboardGames();
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);

  const effectiveGameId = selectedGameId ?? (games.length > 0 ? games[0].gameConfigId : null);
  const selectedGame = useMemo(
    () => games.find((g) => g.gameConfigId === effectiveGameId),
    [games, effectiveGameId],
  );

  const events = selectedGame?.events ?? [];
  const effectiveEvent =
    selectedEvent && events.includes(selectedEvent) ? selectedEvent : (events[0] ?? null);

  const {
    data: leaderboard,
    loading: lbLoading,
    error: lbError,
  } = useLeaderboard(effectiveGameId, effectiveEvent);

  if (gamesLoading) return <Skeleton className="h-64 rounded-lg" />;
  if (gamesError || lbError) return <ErrorBanner message={gamesError || lbError!} />;
  if (games.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No games with leaderboards available yet.
        </CardContent>
      </Card>
    );
  }

  const isHighScore = selectedGame?.leaderboardType === 'high_score';

  return (
    <div className="space-y-6">
      <div className="flex gap-4 flex-wrap items-center">
        <select
          className={selectClass}
          value={effectiveGameId ?? ''}
          onChange={(e) => {
            setSelectedGameId(e.target.value);
            setSelectedEvent(null);
          }}
          aria-label="Leaderboard game"
        >
          {games.map((g) => (
            <option key={g.gameConfigId} value={g.gameConfigId}>
              {g.displayName}
            </option>
          ))}
        </select>

        {events.length > 1 && (
          <select
            className={selectClass}
            value={effectiveEvent ?? ''}
            onChange={(e) => setSelectedEvent(e.target.value)}
            aria-label="Leaderboard event"
          >
            {events.map((ev) => (
              <option key={ev} value={ev}>
                {formatEventLabel(ev)}
              </option>
            ))}
          </select>
        )}

        <span className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
          {isHighScore ? (
            <>
              <ArrowUp className="h-3 w-3" /> High Score
            </>
          ) : (
            <>
              <ArrowDown className="h-3 w-3" /> Low Score
            </>
          )}
        </span>
      </div>

      {lbLoading ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : leaderboard && leaderboard.entries.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Trophy className="h-4 w-4" /> Rankings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium w-16">Rank</th>
                    <th className="text-left py-2 font-medium">Player</th>
                    <th className="text-right py-2 font-medium">Best Score</th>
                    <th className="text-right py-2 font-medium">Plays</th>
                    <th className="text-right py-2 font-medium">Last Played</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.entries.map((entry) => {
                    const isCurrentUser = entry.userId === user?.userId;
                    return (
                      <tr
                        key={entry.rank}
                        className={`border-b last:border-0 ${isCurrentUser ? 'bg-primary/5 font-medium' : ''}`}
                      >
                        <td className="py-2">{rankCell(entry.rank)}</td>
                        <td className="py-2">
                          {playerName(entry)}
                          {isCurrentUser && (
                            <span className="ml-2 text-xs text-primary">(you)</span>
                          )}
                        </td>
                        <td className="text-right py-2">{entry.bestScore.toLocaleString()}</td>
                        <td className="text-right py-2 text-muted-foreground">
                          {entry.playCount.toLocaleString()}
                        </td>
                        <td className="text-right py-2 text-muted-foreground">
                          {new Date(entry.lastPlayedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {leaderboard.myRank && !leaderboard.entries.some((e) => e.userId === user?.userId) && (
              <div className="mt-4 p-3 rounded-md bg-muted/50 text-sm">
                Your rank: <strong>#{leaderboard.myRank.rank}</strong> with a score of{' '}
                <strong>{leaderboard.myRank.bestScore.toLocaleString()}</strong>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No leaderboard entries yet for this game event.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Time-played leaderboard
// ============================================================================

function TimeLeaderboard() {
  const { user } = useAuth();
  const { games, loading: gamesLoading, error: gamesError } = useTimeGames();
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const effectiveGameId = selectedGameId ?? (games.length > 0 ? games[0].gameConfigId : null);

  const { data, loading, error } = useTimePlayedLeaderboard(effectiveGameId);

  if (gamesLoading) return <Skeleton className="h-64 rounded-lg" />;
  if (gamesError || error) return <ErrorBanner message={gamesError || error!} />;
  if (games.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No games have tracked play time yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-4 flex-wrap">
        <select
          className={selectClass}
          value={effectiveGameId ?? ''}
          onChange={(e) => setSelectedGameId(e.target.value)}
          aria-label="Time leaderboard game"
        >
          {games.map((g) => (
            <option key={g.gameConfigId} value={g.gameConfigId}>
              {g.displayName}
            </option>
          ))}
        </select>
      </div>

      {data && (
        <p className="text-sm text-muted-foreground">
          Community total: <strong>{formatTime(data.community.totalSeconds)}</strong> across{' '}
          <strong>{data.community.uniquePlayers.toLocaleString()}</strong>{' '}
          {data.community.uniquePlayers === 1 ? 'player' : 'players'} (
          {data.community.totalSessions.toLocaleString()} sessions)
        </p>
      )}

      {loading ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : data && data.entries.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4" /> Most Time Played
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium w-16">Rank</th>
                    <th className="text-left py-2 font-medium">Player</th>
                    <th className="text-right py-2 font-medium">Time</th>
                    <th className="text-right py-2 font-medium">Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((entry) => {
                    const isCurrentUser = entry.userId === user?.userId;
                    return (
                      <tr
                        key={entry.rank}
                        className={`border-b last:border-0 ${isCurrentUser ? 'bg-primary/5 font-medium' : ''}`}
                      >
                        <td className="py-2">{rankCell(entry.rank)}</td>
                        <td className="py-2">
                          {playerName(entry)}
                          {isCurrentUser && (
                            <span className="ml-2 text-xs text-primary">(you)</span>
                          )}
                        </td>
                        <td className="text-right py-2">{formatTime(entry.totalSeconds)}</td>
                        <td className="text-right py-2 text-muted-foreground">
                          {entry.sessionCount.toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {data.myRank && !data.entries.some((e) => e.userId === user?.userId) && (
              <div className="mt-4 p-3 rounded-md bg-muted/50 text-sm">
                Your rank: <strong>#{data.myRank.rank}</strong> with{' '}
                <strong>{formatTime(data.myRank.totalSeconds)}</strong> played
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No play time recorded for this game yet.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Reward-earnings leaderboard
// ============================================================================

const ALL_GAMES = '__all__';

function RewardLeaderboard() {
  const { user } = useAuth();
  const { games, loading: gamesLoading, error: gamesError } = useTimeGames();
  const { rewardTypes, loading: typesLoading, error: typesError } = useRewardTypes();

  // Default game = All games; default reward type = first available.
  const [selectedGame, setSelectedGame] = useState<string>(ALL_GAMES);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const effectiveType = selectedType ?? (rewardTypes.length > 0 ? rewardTypes[0] : null);
  const gameConfigId = selectedGame === ALL_GAMES ? undefined : selectedGame;

  const { data, loading, error } = useRewardLeaderboard(effectiveType, gameConfigId);

  if (typesLoading || gamesLoading) return <Skeleton className="h-64 rounded-lg" />;
  if (typesError || gamesError || error) {
    return <ErrorBanner message={typesError || gamesError || error!} />;
  }
  if (rewardTypes.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No rewards have been earned yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-4 flex-wrap">
        <select
          className={selectClass}
          value={effectiveType ?? ''}
          onChange={(e) => setSelectedType(e.target.value)}
          aria-label="Reward type"
        >
          {rewardTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          value={selectedGame}
          onChange={(e) => setSelectedGame(e.target.value)}
          aria-label="Reward leaderboard game"
        >
          <option value={ALL_GAMES}>All games</option>
          {games.map((g) => (
            <option key={g.gameConfigId} value={g.gameConfigId}>
              {g.displayName}
            </option>
          ))}
        </select>
      </div>

      {data && effectiveType && (
        <p className="text-sm text-muted-foreground">
          {gameConfigId ? 'Total earned in this game' : 'Total earned by everyone'}:{' '}
          <strong>
            {data.communityTotal.toLocaleString()} {effectiveType}
          </strong>
        </p>
      )}

      {loading ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : data && data.entries.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Gift className="h-4 w-4" /> Top {effectiveType} Earners
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium w-16">Rank</th>
                    <th className="text-left py-2 font-medium">Player</th>
                    <th className="text-right py-2 font-medium">{effectiveType} Earned</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((entry) => {
                    const isCurrentUser = entry.userId === user?.userId;
                    return (
                      <tr
                        key={entry.rank}
                        className={`border-b last:border-0 ${isCurrentUser ? 'bg-primary/5 font-medium' : ''}`}
                      >
                        <td className="py-2">{rankCell(entry.rank)}</td>
                        <td className="py-2">
                          {playerName(entry)}
                          {isCurrentUser && (
                            <span className="ml-2 text-xs text-primary">(you)</span>
                          )}
                        </td>
                        <td className="text-right py-2">{entry.totalAmount.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {data.myRank && !data.entries.some((e) => e.userId === user?.userId) && (
              <div className="mt-4 p-3 rounded-md bg-muted/50 text-sm">
                Your rank: <strong>#{data.myRank.rank}</strong> with{' '}
                <strong>
                  {data.myRank.totalAmount.toLocaleString()} {effectiveType}
                </strong>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No one has earned {effectiveType} here yet.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Page
// ============================================================================

function AnonymityToggle() {
  const { anonymous, loading, saving, setAnonymous } = useLeaderboardAnonymity();
  return (
    <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
      <input
        type="checkbox"
        className="h-4 w-4"
        checked={anonymous}
        disabled={loading || saving}
        onChange={(e) => setAnonymous(e.target.checked)}
      />
      Make me anonymous in the leaderboards
    </label>
  );
}

export function LeaderboardPage() {
  return (
    <PageShell>
      <div>
        {backToDashboardLink}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-2xl font-semibold">Leaderboards</h1>
          <AnonymityToggle />
        </div>
      </div>

      <Tabs defaultValue="score">
        <TabsList>
          <TabsTrigger value="score">Score</TabsTrigger>
          <TabsTrigger value="time">Time Played</TabsTrigger>
          <TabsTrigger value="rewards">Rewards</TabsTrigger>
        </TabsList>
        <TabsContent value="score" className="mt-4">
          <ScoreLeaderboard />
        </TabsContent>
        <TabsContent value="time" className="mt-4">
          <TimeLeaderboard />
        </TabsContent>
        <TabsContent value="rewards" className="mt-4">
          <RewardLeaderboard />
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
